import { Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {RicsStandardUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    RicsStandard,
    RicsStandardOnServerCreated,
    selectRicsStandardsActionLoading,
    selectRicsStandardById,
    selectLastCreatedRicsStandardId
} from '../../../../core/linked-tables';

@Component({
    selector: 'kt-rics-standard-edit',
    templateUrl: './rics-standard-edit.component.html',
    styleUrls: ['./rics-standard-edit.component.scss'],
})


export class RicsStandardEditComponent implements OnInit, OnDestroy {

    model: RicsStandard;
    oldModel: RicsStandard;

    ricsStandardForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param subheaderService
     * @param activatedRoute
     * @param router
     * @param fb: FormBuilder
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectRicsStandardsActionLoading));
        const routeSubscription = this.activatedRoute.params.subscribe(params => {
            const id = params.id;
            if (id && id > 0) {
                this.store.pipe(select(selectRicsStandardById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initRicsStandard();
                    }
                });
            } else {
                this.model = new RicsStandard();
                this.model.clear();
                this.initRicsStandard();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../admin-management`},
            {title: 'Linked tables', page: `../admin-management/linked-tables`},
            {title: 'Interaction types', page: `../admin-management/linked-tables/rics-standards`},
            {
                title: this.getComponentTitle(), page: id > 0 ? `../admin-management/linked-tables/rics-standards/edit/${id}`
                    : `../admin-management/linked-tables/rics-standards/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initRicsStandard() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.ricsStandardForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit RICS Standards '${
                this.model.name
                }'`;
        }
        return 'New RICS Standards';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.ricsStandardForm.controls[controlName];
        const result = control.invalid && control.touched;
        return result;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.ricsStandardForm.markAsPristine();
        this.ricsStandardForm.markAsUntouched();
        this.ricsStandardForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.ricsStandardForm.controls;
        /** check form */
        if (this.ricsStandardForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateRicsStandard(this.model);
        } else {
            this.createRicsStandard(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: RicsStandardModel
     */
    updateRicsStandard(_model: RicsStandard) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateRicsStandard: Update<RicsStandard> = {
            id: _model.id,
            changes: _model
        };

        this.store.dispatch(new RicsStandardUpdated({
            partialricsStandard: updateRicsStandard,
            ricsStandard: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _model: RicsStandardModel
     */
    createRicsStandard(_model: RicsStandard) {
        this.store.dispatch(new RicsStandardOnServerCreated({ricsStandard: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedRicsStandardId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

}
